import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { ConfirmModal } from '@gitbook/react-uikit';

type ConfirmActionCallback = () => void | Promise<void>;

export type ConfirmWrapCallback = (
    confirm: api.ContentKitConfirm | undefined,
    callback: ConfirmActionCallback
) => void | Promise<void>;

/**
 * Utility to handle a potential confirm modal before executing a callback.
 */
export function useContentKitConfirm(): [React.ReactElement | null, ConfirmWrapCallback] {
    const [modal, setModal] = React.useState<
        { confirm: api.ContentKitConfirm; callback: ConfirmActionCallback } | undefined
    >(undefined);

    const onCompleteConfirm = (confirmed: boolean) => {
        if (modal && confirmed) {
            const result = modal.callback();
            if (result instanceof Promise) {
                result.then(
                    () => setModal(undefined),
                    () => setModal(undefined)
                );
            } else {
                setModal(undefined);
            }

            return result;
        }
        setModal(undefined);
    };

    return [
        modal ? (
            <ConfirmModal
                kind={modal.confirm.style}
                title={modal.confirm.title}
                confirmButtonText={modal.confirm.confirm}
                onComplete={onCompleteConfirm}
            >
                {modal.confirm.text}
            </ConfirmModal>
        ) : null,
        React.useCallback((confirm, callback) => {
            if (confirm) {
                setModal({ confirm, callback });
                return;
            }

            return callback();
        }, []),
    ];
}
